/**
 * PHARMAKON — barcodes · the Pharmakon lot code (D-15, D-16, 03_DATABASE DB-07, DB-OP-04).
 *
 * 12 digits = prefix « 2 » + 10-digit per-pharmacy sequence (zero-padded) + GS1 mod-10 check digit,
 * printed as Code 128 subset C (6 data symbols). The leading 2 is the GS1 « restricted circulation /
 * in-store » number system of the 12-digit UPC-A range, so no manufacturer box carries such a code;
 * the check digit lets the resolver reject a misread before any lookup.
 *
 * One recogniser for the server resolver (GET /codes/resolve), the SPA scan surfaces and the
 * label sheet: `analyserCodeLot`. Formatting: `formerCodeLot`.
 *
 * Canonical source: packages/shared/barcode/ (copy in apps/server/src/utils/barcode/, see gs1.js).
 */
import { avecCleGs1, estCleGs1Valide } from "./gs1.js";

/** DB-OP-04 — the lot code format (one definition; the server CONFIG re-exports it). */
export const CODE_LOT_FORMAT = Object.freeze({
  PREFIXE: "2",
  CHIFFRES_SEQUENCE: 10,
  LONGUEUR: 12,
});

/** Largest sequence number the 10-digit field can carry. */
export const CODE_LOT_SEQUENCE_MAX = 10 ** CODE_LOT_FORMAT.CHIFFRES_SEQUENCE - 1;

const FORME_CODE_LOT = new RegExp(`^${CODE_LOT_FORMAT.PREFIXE}\\d{${CODE_LOT_FORMAT.LONGUEUR - 1}}$`);

/** Why a scanned text is not a valid lot code. */
export const RAISON_CODE_LOT = Object.freeze({
  /** Not shaped like a Pharmakon lot code (length, prefix, non-digits) — maybe a product barcode. */
  FORMAT: "FORMAT",
  /** Shaped like a lot code, wrong check digit — a misread. */
  CLE_INVALIDE: "CLE_INVALIDE",
});

/**
 * The lot code of sequence number `n`.
 *
 * @param {number} n integer, 1 … CODE_LOT_SEQUENCE_MAX
 * @returns {string} 12 digits
 */
export function formerCodeLot(n) {
  if (!Number.isSafeInteger(n) || n < 1 || n > CODE_LOT_SEQUENCE_MAX) {
    const e = new RangeError(`Numéro de séquence de code lot invalide : ${String(n)}`);
    e.code = "CODE_LOT_SEQUENCE_INVALIDE";
    throw e;
  }
  return avecCleGs1(`${CODE_LOT_FORMAT.PREFIXE}${String(n).padStart(CODE_LOT_FORMAT.CHIFFRES_SEQUENCE, "0")}`);
}

/**
 * Recognises a scanned or typed text as a Pharmakon lot code (surrounding whitespace ignored).
 *
 * @param {string} texte
 * @returns {{ valide: true, code: string, sequence: number }
 *         | { valide: false, raison: "FORMAT"|"CLE_INVALIDE", code: string }}
 */
export function analyserCodeLot(texte) {
  const code = typeof texte === "string" ? texte.trim() : "";
  if (!FORME_CODE_LOT.test(code)) return { valide: false, raison: RAISON_CODE_LOT.FORMAT, code };
  if (!estCleGs1Valide(code)) return { valide: false, raison: RAISON_CODE_LOT.CLE_INVALIDE, code };
  return {
    valide: true,
    code,
    sequence: Number(code.slice(CODE_LOT_FORMAT.PREFIXE.length, -1)),
  };
}

/** True when `texte` is a valid Pharmakon lot code. */
export function estCodeLot(texte) {
  return analyserCodeLot(texte).valide;
}
